import React from "react"
import {
	Box,
	Grid,
	TextField,
	Typography,
} from "@mui/material"
import {
	t,
	Trans,
} from "@lingui/macro"
import {
	MatesDatePicker,
	MatesSelect,
	MatesTextField,
	withUseFormHook,
} from "@sinossi/mates-react-library"
import { SbDialog } from "components/commons/sb-dialog"
import {
	Asset,
	ContractType,
} from "models"
import { UseFormReturn } from "react-hook-form"
import { Autocomplete } from "@mui/lab"
import { Editor } from "react-draft-wysiwyg"
import {
	convertFromRaw,
	convertToRaw,
	EditorState,
} from "draft-js"
import {
	BrandOption,
	PrecompiledTemplate,
	SendNewProposalRequest,
} from "components/match-proposal/model"
import SendNewProposalValidation, { SendNewProposalForm } from "./form"
import { DateTime } from "luxon"

interface SendNewProposalProps extends UseFormReturn {
	asset: Asset
	precompiled?: boolean
	onClose: () => void
	submit: (request: SendNewProposalRequest) => void
	selectedBrands: BrandOption[]
	allBrands: BrandOption[]
}

interface SendNewProposalState {
	editorState: EditorState
	selectedBrands: BrandOption[]
}

class SendNewProposal extends React.Component<SendNewProposalProps, SendNewProposalState>
{
	static defaultProps = {
		precompiled: false
	}

	constructor(props: SendNewProposalProps)
	{
		super(props)
		this.state = {
			editorState: EditorState.createEmpty(),
			selectedBrands: this.props.selectedBrands
		}
	}

	componentDidMount() {
		if(this.props.precompiled){
			const contentState = convertFromRaw(PrecompiledTemplate)
			this.setState(() => ({
				editorState: EditorState.createWithContent(contentState),
			}))

			let form = new SendNewProposalForm()
			let expire = new Date()
			expire.setDate(expire.getDate() + 30)

			form.price = this.props.asset.characteristics.price.toFixed(2)
			form.type = this.props.asset.characteristics.contractType
			form.object = `Proposta per l'immobile sito in ${this.props.asset.address}`
			form.expireDate = DateTime.fromISO(expire.toISOString())

			this.props.reset(form)
		}
	}

	private submit = () => {
		const editorValue = convertToRaw(this.state.editorState.getCurrentContent())
		const formValues = this.props.getValues() as SendNewProposalForm

		if(
			this.state.selectedBrands.length > 0 &&
			!Object.values(formValues).some(value => !value && (typeof value === "string" && value !== ""))){
			let request: SendNewProposalRequest = {
				assetId: this.props.asset.id,
				brandIds: this.state.selectedBrands.map(brand => brand.id),
				subject: formValues.object!,
				type: formValues.type!,
				expirationDate: new Date(formValues.expireDate!.toString()),
				price: parseFloat(formValues.price!),
				message: editorValue
			}

			this.props.submit(request)
		}
	}

	private handleEditorChange = (editorState: EditorState) => {
		this.setState(() => ({
			editorState: editorState,
		}))
	}

	render()
	{
		return(
			<SbDialog
				titleText={t({id: "send-new-proposal.send-modal", message: `Proposta per immobile ${this.props.asset.address}`})}
				width={"lg"}
				height="80vh"
				confirmButton
				closeButton
				onClose={this.props.onClose}
				onConfirm={this.submit}
			>
				<Grid container spacing={3} sx={{mt: 1}}>
					<Grid item xs={12} md={4}>
						<Box sx={{mb: 2.5}}>
							<Autocomplete
								multiple
								getOptionLabel={(option) => option.name}
								filterOptions={(x) => x}
								options={this.props.allBrands}
								autoComplete
								includeInputInList
								filterSelectedOptions
								value={this.state.selectedBrands}
								noOptionsText={t({ id: "snp.all-brands-selected", message: "Non ci sono altre opzioni disponibili" })}
								onChange={(event, newValue) => {
									if (newValue) {
										this.setState(() => ({
											selectedBrands: newValue,
										}))
									}
								}}
								renderInput={(params) => (
									<TextField {...params}
										label={t({ id: "snp.selected-brands", message: "Invia a..." })}
										fullWidth
										size="small"
									/>
								)}
							/>
						</Box>
						<MatesTextField variant={'outlined'}
							label={t({
								id: "snp.send-object",
								message: "Oggetto",
							})}
							name="object"
							control={this.props.control}
							trigger={this.props.trigger}
							formState={this.props.formState}
							sx={{ mb: 2.5 }}
						/>
						<MatesSelect variant={"outlined"}
							label={t({
									id: "snp.match-type",
									message: "Tipo",
								}
							)}
							name="type"
							control={this.props.control}
							trigger={this.props.trigger}
							formState={this.props.formState}
							sx={{ mb: 2.5 }}
						>
							{
								Object.keys(ContractType).map(key => (
									<option key={key} value={key}>
										<Trans id={`enums.contract-type.${key}`}>
											{key}
										</Trans>
									</option>
								))
							}
						</MatesSelect>

						<MatesDatePicker variant="outlined"
							label={t({
								id: "snp.expire-date",
								message: "Data di scadenza",
							})}
							name="expireDate"
							control={this.props.control}
							trigger={this.props.trigger}
							formState={this.props.formState}
							minDate={DateTime.fromISO(new Date().toISOString())}
							sx={{ mb: 2.5 }}
						/>
						<MatesTextField variant={'outlined'}
							label={t({
								id: "snp.price",
								message: "Prezzo",
							})}
							name="price"
							control={this.props.control}
							trigger={this.props.trigger}
							formState={this.props.formState}
							sx={{ mb: 2 }}
							endAdornment={"€"}
						/>
					</Grid>
					<Grid item xs={12} md={8}>
						<Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
							<Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
								<Trans id="snp.message-content">Contenuto del messaggio</Trans>
							</Typography>
							<Editor
								editorState={this.state.editorState}
								onEditorStateChange={this.handleEditorChange}
								toolbar={{
									options: ['inline', 'list', 'link'],
									inline: {
										options: ['bold', 'italic'],
									},
									list: {
										options: ['unordered', 'ordered'],
									},
									link: {
										options: ['link'],
									},
								}}
								toolbarStyle={{
									backgroundColor: "#11347B",
									borderColor: "#11347B",
									borderRadius: "4px 4px 0 0"
								}}
								editorStyle={{
									minHeight: "350px",
									maxHeight: "450px",
									border: "1px solid #c4c4c4",
									borderTop: "none",
									borderRadius: "0 0 4px 4px",
									padding: "12px",
									overflowY: "auto"
								}}
							/>
						</Box>
					</Grid>
				</Grid>
			</SbDialog>
		)
	}
}

export default withUseFormHook<{}>(
	SendNewProposal,
	SendNewProposalValidation,
	{}
)
